import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, Wind } from "lucide-react";
import TopbarInner from "../components/topbar/TopbarInner";
import { audioManager } from "../utils/AudioManager";
import { armMediaGroup, pauseAndResetMedia, startMediaGroup } from "../utils/mediaStart";
import { resolveAudioSource } from "../services/premiumAudio.js";
import { CROSSFADE_SECONDS } from "../services/ai/composer/sessionArc.js";
import { saveSessionStart } from "../services/ai/memory/aiMemory.js";
import AISessionFeedback from "../components/ai/AISessionFeedback";
import "./AIPlayerPage.css";
import BreathingAnimation from "../components/BreathingAnimation";
import { CountdownOverlay, FullscreenBreathing } from "./PlayerPage";

const START_FADE_MS = 700;
const BREATHING_START_FADE_MS = 260;
const REVEAL_REANCHOR_TOLERANCE = 0.06;
const FORCED_SYNC_TOLERANCE = 0.04;
const AMBIANCE_START_FADE_MS = 500;
const PAUSE_FADE_MS = 200;
const RESUME_FADE_MS = 220;
const FINAL_FADE_MS = 2000;
const FINAL_FADE_LEAD_SECONDS = FINAL_FADE_MS / 1000;
const PROGRESS_PAINT_MS = 50;
const NATURAL_GAIN = 1.0;
const FEEDBACK_MIN_LISTEN_SECONDS = 20;
const BREATHING_VOLUME_RATIO = 1.5;
const DURATION_SECONDS = Object.freeze({
  "30sec": 30,
  "2min": 120,
  "5min": 300,
  "10min": 600,
});

const getBaseVolumeKey = (uid) => `harmonauria_base_volume:${uid || "guest"}`;

function waitUntilPlayable(element, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    if (!element || !element.src) {
      reject(new Error("Piste audio absente."));
      return;
    }

    if (element.readyState >= 3) {
      resolve(true);
      return;
    }

    let timeoutId = 0;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      element.removeEventListener("canplay", onReady);
      element.removeEventListener("loadeddata", onReady);
      element.removeEventListener("error", onError);
    };

    const onReady = () => {
      if (element.readyState < 3) return;
      cleanup();
      resolve(true);
    };

    const onError = () => {
      cleanup();
      reject(new Error("Impossible de charger une piste audio."));
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Le chargement audio a expiré."));
    }, timeoutMs);

    element.addEventListener("canplay", onReady, { once: true });
    element.addEventListener("loadeddata", onReady, { once: true });
    element.addEventListener("error", onError, { once: true });
  });
}

const BRAINWAVE_LABELS = Object.freeze({
  delta: "Delta",
  theta: "Thêta",
  alpha: "Alpha",
  beta: "Bêta",
  gamma: "Gamma",
});

function normalizeFrequencyValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${Number.isInteger(numeric) ? numeric : numeric.toFixed(2)} Hz`;
}

function buildSessionFrequencies(tracks = []) {
  const items = [];
  const seen = new Set();

  for (const track of tracks) {
    const explicit = Array.isArray(track?.frequencies) ? track.frequencies : [];

    if (explicit.length) {
      for (const frequency of explicit) {
        const hz = frequency?.hz || normalizeFrequencyValue(frequency?.value);
        const type = String(frequency?.type || track?.brainwave || "").trim();
        if (!hz && !type) continue;

        const key = `${hz || ""}|${type.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);

        items.push({
          hz: hz || "—",
          type: BRAINWAVE_LABELS[type.toLowerCase()] || type || "—",
          note: frequency?.note || (track?.tuning ? `Accordage ${track.tuning} Hz` : null),
        });
      }
      continue;
    }

    const hz = normalizeFrequencyValue(track?.fundamentalHz);
    const rawType = String(track?.brainwave || "").trim();
    if (!hz && !rawType) continue;

    const key = `${hz || ""}|${rawType.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);

    items.push({
      hz: hz || "—",
      type: BRAINWAVE_LABELS[rawType.toLowerCase()] || rawType || "—",
      note: track?.tuning ? `Accordage ${track.tuning} Hz` : null,
    });
  }

  return items;
}

/* ============================================================
   NATURAL SOUND TILE
============================================================ */
const NaturalSoundTile = memo(function NaturalSoundTile({
  sound,
  active,
  loading = false,
  onToggle,
  onVolumeChange,
  initialVolume = 0.7,
}) {
  const volumeRef = useRef(initialVolume);
  const lastClickRef = useRef(0);

  useEffect(() => {
    volumeRef.current = initialVolume;
  }, [initialVolume]);

  const handleToggle = useCallback(() => {
    if (loading) return;

    const now = Date.now();
    if (now - lastClickRef.current < 300) return;

    lastClickRef.current = now;
    onToggle(sound.id, volumeRef.current);
  }, [loading, onToggle, sound.id]);

  const handleVolumeInput = useCallback((event) => {
    const nextVolume = Math.max(0, Math.min(1, Number(event.currentTarget.value) || 0));

    volumeRef.current = nextVolume;
    onVolumeChange(sound.id, nextVolume);
  }, [onVolumeChange, sound.id]);

  return (
    <div className="natural-sound-item">
      <button
        type="button"
        onClick={(event) => { handleToggle(); event.currentTarget.blur(); }}
        disabled={loading}
        aria-busy={loading}
        className={`natural-sound-button ${active ? "active" : ""}`}
        aria-pressed={active}
        aria-label={sound.name}
      >
        <div className="sound-emoji-circle">
          <img
            src={sound.image}
            alt={sound.name}
            className="sound-icon-img"
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
        </div>

        <span className="sound-name">{sound.name}</span>

        {(loading || active) && (
          <span className="sound-indicator" aria-hidden="true">
            {loading ? "⏳" : <Volume2 size={12} />}
          </span>
        )}
      </button>

      {active && (
        <div className="volume-control">
          <Volume2 size={14} className="volume-icon" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            defaultValue={volumeRef.current}
            onInput={handleVolumeInput}
            className="volume-slider"
          />
        </div>
      )}
    </div>
  );
});

/* ============================================================
   PLAYER PRINCIPAL
============================================================ */
function AIPlayerPage({
  user,
  naturalSounds,
  isPremium,
  longestStreak = 0,
  markTodayActive,
  onFinalize,
  onSaveNote,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const historyGuardInstalledRef = useRef(false);
  const historyGuardActiveRef = useRef(false);
  const suppressBrowserBackRef = useRef(false);
  const browserBackHandledRef = useRef(false);

const rawSession = location.state?.session;

/* L'API History clone le state à chaque navigate : la référence change alors
   que le contenu est identique. La session est donc identifiée logiquement. */
const sessionKey = useMemo(() => {
  if (!rawSession) return null;
  if (rawSession.id) return String(rawSession.id);
  const trackIds = (rawSession.arc?.tracks || []).map((track) => track?.id).join("|");
  return `${trackIds}:${rawSession.context?.durationKey || rawSession.durationKey || ""}:${rawSession.context?.generatedAt || ""}`;
}, [rawSession]);

const session = useMemo(() => {
  if (!rawSession) return null;

  const normalizedBreathing =
    rawSession.breathing ||
    rawSession.context?.breathing ||
    (rawSession.context?.breathingFile
      ? {
          id: rawSession.context.breathingId || "custom",
          name: rawSession.context.breathingName || "Respiration guidée",
          file: rawSession.context.breathingFile,
        }
      : null);

  return {
    ...rawSession,
    breathing: normalizedBreathing,
    context: {
      ...(rawSession.context || {}),
      breathing: normalizedBreathing,
    },
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [sessionKey]);

const stateBefore =
  location.state?.stateBefore ?? session?.context?.stateBefore ?? 5;
const intent =
  location.state?.intent ?? session?.context?.intent ?? "relax";
const isReplay = session?.context?.replayedFromFavorite === true;

const hasBreathingAudio = Boolean(session?.breathing?.file);

  useEffect(() => {
    if (!session || historyGuardInstalledRef.current) return;

    historyGuardInstalledRef.current = true;

    if (location.state?.__auriaPlayerGuard === true) {
      historyGuardActiveRef.current = true;
      return;
    }

    historyGuardActiveRef.current = true;

    navigate(location.pathname, {
      replace: false,
      state: {
        ...(location.state || {}),
        __auriaPlayerGuard: true,
      },
    });
  }, [session, navigate, location.pathname, location.state]);

  const onDoneRedirect = useCallback(() => {
    const replaceWithHome = () => {
      navigate("/", { replace: true });
    };

    if (!historyGuardActiveRef.current) {
      replaceWithHome();
      return;
    }

    suppressBrowserBackRef.current = true;

    const removeGuardAndLeave = () => {
      historyGuardActiveRef.current = false;
      suppressBrowserBackRef.current = false;
      replaceWithHome();
    };

    window.addEventListener("popstate", removeGuardAndLeave, {
      once: true,
    });

    window.history.back();
  }, [navigate]);

  useEffect(() => {
    if (!session) onDoneRedirect();
  }, [session, onDoneRedirect]);

  // ===== STATES =====
  const [currentTrackIdx, setCurrentTrackIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);

  const [globalElapsed, setGlobalElapsed] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [listenedSeconds, setListenedSeconds] = useState(0);
  const [feedbackSession, setFeedbackSession] = useState(null);
const [baseVolume, setBaseVolume] = useState(() => {
  try {
    const v = parseFloat(localStorage.getItem(getBaseVolumeKey(user?.uid)));
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 1;
  } catch {
    return 1;
  }
});
const baseVolumeLiveRef = useRef(baseVolume);
// Recharge le volume quand l'uid change (changement de compte)
useEffect(() => {
  let nextVolume = 1;

  try {
    const storedVolume = parseFloat(localStorage.getItem(getBaseVolumeKey(user?.uid)));
    nextVolume = Number.isFinite(storedVolume) && storedVolume >= 0 && storedVolume <= 1 ? storedVolume : 1;
  } catch {}

  baseVolumeLiveRef.current = nextVolume;
  setBaseVolume(nextVolume);
}, [user?.uid]);

// Sauvegarde à chaque changement
useEffect(() => {
  try {
    localStorage.setItem(getBaseVolumeKey(user?.uid), String(baseVolume));
  } catch {}
}, [baseVolume, user?.uid]);

  const [showVolume, setShowVolume] = useState(false);
  const [isBreathingMuted, setIsBreathingMuted] = useState(false);

  const [activeNaturals, setActiveNaturals] = useState(new Set());
  const [naturalLoading, setNaturalLoading] = useState({});

  const setNaturalLoadingFlag = useCallback((id, v) => {
    setNaturalLoading((prev) => ({ ...prev, [id]: v }));
  }, []);

  // ===== REFS =====
  const aiSessionLoggedRef = useRef(false);
  const sessionStartLoggedRef = useRef(false);
  const trackARef = useRef(null);
  const trackBRef = useRef(null);
  const breathingRef = useRef(null);
  const activeKeyRef = useRef("A");
  const isPlayingRef = useRef(false);
  const crossfadingRef = useRef(false);
  const crossfadeStateRef = useRef(null);
  const transportShouldPlayRef = useRef(false);
  const transportArmedRef = useRef(false);
  const startupPreparingRef = useRef(false);
  const breathingStartEnabledRef = useRef(hasBreathingAudio);
  const bufferingRef = useRef(false);
  const bufferingTimerRef = useRef(null);

  const naturalRefs = useRef({});
  const naturalVolumesRef = useRef({});
  const naturalAbortRef = useRef({});
  const initialAmbiancesAppliedRef = useRef(false);
  const naturalPreloadPromiseRef = useRef({});
  const activeNaturalsRef = useRef(new Set());
  const naturalOpTokenRef = useRef({});

  const volumeBtnRef = useRef(null);
  const volumePopoverRef = useRef(null);
  const finishedRef = useRef(false);
const finalFadeStartedRef = useRef(false);
const finalFadeEndAtRef = useRef(0);
const finalFadeTimeoutRef = useRef(null);
const progressRafRef = useRef(0);
const lastProgressPaintRef = useRef(0);
  const elapsedRef = useRef(0);
  const progressEpochRef = useRef(0);
  const streakCountedRef = useRef(false);
  const wakeLockRef = useRef(null);
  const pendingTimeoutsRef = useRef(new Set());
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [isBreathingFullscreen, setIsBreathingFullscreen] = useState(false);

  const trackedTimeout = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      pendingTimeoutsRef.current.delete(id);
      fn();
    }, ms);

    pendingTimeoutsRef.current.add(id);
    return id;
  }, []);

  const finishSessionRef = useRef(null);

  const aiLayersStartedRef = useRef(false);

  const durationKey = session?.context?.durationKey || session?.durationKey || "";
  const durationLimit = DURATION_SECONDS[durationKey] || 0;
  const arcDuration = Number(session?.arc?.totalDuration) || 0;
  const totalDuration = durationLimit && arcDuration ? Math.min(durationLimit, arcDuration) : durationLimit || arcDuration;
  const tracks = useMemo(() => session?.arc?.tracks || [], [session]);
  const frequencyDetails = useMemo(() => buildSessionFrequencies(tracks), [tracks]);
  const aiAmbiances = useMemo(() => session?.ambiances || [], [session]);

  const cumulativeStartTimes = useMemo(() => {
    const times = [0];
    let acc = 0;
    for (let i = 0; i < tracks.length - 1; i++) {
      acc += tracks[i].playUntil - CROSSFADE_SECONDS;
      times.push(Math.max(0, acc));
    }
    return times;
  }, [tracks]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    elapsedRef.current = globalElapsed;
  }, [globalElapsed]);

  useEffect(() => {
    activeNaturalsRef.current = new Set(activeNaturals);
  }, [activeNaturals]);

  const requestWakeLock = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    try {
      if (wakeLockRef.current) return;
      wakeLockRef.current = await navigator.wakeLock.request("screen");
    } catch {}
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch {}
      wakeLockRef.current = null;
    }
  }, []);

  const getActiveAudio = useCallback(() => {
    return activeKeyRef.current === "A" ? trackARef.current : trackBRef.current;
  }, []);

  const getActiveKey = useCallback(() => {
    return `ai-track${activeKeyRef.current}`;
  }, []);

  const preloadOneNatural = useCallback((ns) => {
    const id = ns.id;
    if (naturalPreloadPromiseRef.current[id]) return naturalPreloadPromiseRef.current[id];
    const ctrl = new AbortController();
    naturalAbortRef.current[id] = ctrl;
    const element = naturalRefs.current[id];
    const promise = (async () => {
      try {
        const source = await resolveAudioSource(ns.file);
        if (ctrl.signal.aborted || !element || naturalRefs.current[id] !== element) return false;
        const absoluteSource = new URL(source, window.location.origin).href;
        if (!element.paused || activeNaturalsRef.current.has(id)) return true;
        element.crossOrigin = "anonymous";
        element.preload = "auto";
        if (element.src !== absoluteSource) { element.src = source; element.load(); }
        await waitUntilPlayable(element, 4000);
        return !ctrl.signal.aborted;
      } catch { return false; }
      finally {
        if (naturalAbortRef.current[id] === ctrl) {
          delete naturalAbortRef.current[id];
          delete naturalPreloadPromiseRef.current[id];
        }
      }
    })();
    naturalPreloadPromiseRef.current[id] = promise;
    return promise;
  }, []);

  useEffect(() => {
  if (isLoading || isCountingDown || !Array.isArray(naturalSounds) || naturalSounds.length === 0) return undefined;

  const activeIds = new Set(aiAmbiances.map((ambiance) => ambiance.id));
  const list = naturalSounds.filter((ns) => (!ns.premium || isPremium) && !activeIds.has(ns.id));
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const concurrency = connection?.saveData ? 1 : 2;
  let cancelled = false;
  let index = 0;

  const run = async () => {
    const workers = Array.from({ length: concurrency }, async () => {
      while (!cancelled && index < list.length) {
        const ns = list[index++];
        await preloadOneNatural(ns);
      }
    });

    await Promise.all(workers);
  };

  void run();

  return () => {
    cancelled = true;
  };
}, [aiAmbiances, isLoading, isCountingDown, isPremium, naturalSounds, preloadOneNatural]);

  // Setup initial
  useLayoutEffect(() => {
    if (!session || !tracks.length) return;

    const a = trackARef.current;
    const b = trackBRef.current;
    if (!a || !b) return;

    finishedRef.current = false;
    finalFadeStartedRef.current = false;
  finalFadeEndAtRef.current = 0;
lastProgressPaintRef.current = 0;
if (finalFadeTimeoutRef.current) {
  window.clearTimeout(finalFadeTimeoutRef.current);
  pendingTimeoutsRef.current.delete(finalFadeTimeoutRef.current);
  finalFadeTimeoutRef.current = null;
}

if (progressRafRef.current) {
  cancelAnimationFrame(progressRafRef.current);
  progressRafRef.current = 0;
}
    crossfadingRef.current = false;
    aiLayersStartedRef.current = false;
    streakCountedRef.current = false;
    sessionStartLoggedRef.current = false;
    activeKeyRef.current = "A";
    elapsedRef.current = 0;
    progressEpochRef.current += 1;
    transportShouldPlayRef.current = false;
    transportArmedRef.current = false;
    startupPreparingRef.current = false;
    breathingStartEnabledRef.current = hasBreathingAudio;
    bufferingRef.current = false;
    crossfadeStateRef.current = null;
    activeNaturalsRef.current = new Set();
    setActiveNaturals(new Set());
    setIsCountingDown(true);
    setIsBreathingFullscreen(false);
    setIsLoading(true);
    setAudioError(null);
    setIsPlaying(false);
    initialAmbiancesAppliedRef.current = false;
    setCurrentTrackIdx(0);
    setGlobalElapsed(0);
    setIsBreathingMuted(false);

    let cancelled = false;

    (async () => {
      try {
       if (!hasBreathingAudio) throw new Error("Cette session ne contient pas de respiration guidée.");
        try {
          void Promise.resolve(audioManager.unlock()).catch(() => {});
        } catch {}

const br = breathingRef.current;
if (!br) throw new Error("Élément audio de respiration absent.");

const [firstSource, breathingSource] = await Promise.all([
  resolveAudioSource(tracks[0].file),
  resolveAudioSource(session.breathing.file),
]);
if (cancelled) return;

a.crossOrigin = "anonymous";
a.preload = "auto";
a.src = firstSource;

if (tracks[1]) {
  void resolveAudioSource(tracks[1].file).then((source) => {
    if (cancelled) return;
    b.crossOrigin = "anonymous";
    b.preload = "auto";
    b.src = source;
    b.load();
  }).catch((error) => { if (!cancelled) console.warn("Préchargement de la piste suivante différé.", error); });
}

try { br.pause(); } catch {}
br.crossOrigin = "anonymous";
br.preload = "auto";
br.loop = true;
br.src = breathingSource;
        audioManager.setupElement(br, "ai-breathing");
        audioManager.setVolume("ai-breathing", 0, 0);
        try { br.load(); } catch {}

        audioManager.setupElement(a, "ai-trackA");
        audioManager.setVolume("ai-trackA", 0, 0);
        audioManager.setupElement(b, "ai-trackB");
        audioManager.setVolume("ai-trackB", 0, 0);

        try {
          a.load();
          // La piste suivante démarre son chargement dès réception de son URL.
        } catch {}

        const ambiancePreloads = (session.ambiances || []).map(async (ambiance) => {
          const file = ambiance.file || (naturalSounds || []).find((sound) => sound.id === ambiance.id)?.file;
          if (!file) throw new Error(`Ambiance introuvable : ${ambiance.id}`);

          const element = naturalRefs.current[ambiance.id];
          if (!element) throw new Error(`Élément audio absent : ${ambiance.id}`);

const source = await resolveAudioSource(file);
if (cancelled) return false;
element.crossOrigin = "anonymous";
element.preload = "auto";
element.src = source;
          try {
            element.load();
          } catch {}

          await waitUntilPlayable(element, 12000);
          return true;
        });

        ambiancePreloads.forEach((promise) => { void promise.catch((error) => { if (!cancelled) console.warn("Ambiance initiale différée.", error); }); });

        await Promise.all([
          waitUntilPlayable(a, 12000),

          waitUntilPlayable(br, 12000).catch(() => {
            throw new Error("Impossible de charger la respiration guidée.");
          }),

        ]);

        if (cancelled) return;
        setIsLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error("AIPlayer load error", e);
        setAudioError(e?.message || "Erreur de chargement");
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
}, [session, tracks, hasBreathingAudio, naturalSounds]);


  // Toggle son naturel manuel
  const toggleNaturalSound = useCallback(
async (soundId, volume = 0.7) => {
  if (finalFadeStartedRef.current || finishedRef.current) return false;

  const ns = naturalSounds.find((sound) => sound.id === soundId);
      if (!ns || (ns.premium && !isPremium)) return false;

      const el = naturalRefs.current[soundId];
      if (!el) return false;

      const token = (naturalOpTokenRef.current[soundId] = (naturalOpTokenRef.current[soundId] || 0) + 1);
      const isOnNow = activeNaturalsRef.current.has(soundId);

      if (isOnNow) {
        activeNaturalsRef.current.delete(soundId);
        setActiveNaturals((prev) => {
          const next = new Set(prev);
          next.delete(soundId);
          return next;
        });
        setNaturalLoadingFlag(soundId, false);
        audioManager.setVolume(`ai-amb-${soundId}`, 0, 220);

        trackedTimeout(() => {
          if (naturalOpTokenRef.current[soundId] !== token) return;
          try {
            el.pause();
            el.currentTime = 0;
          } catch {}
        }, 260);

        return true;
      }

      activeNaturalsRef.current.add(soundId);
      setActiveNaturals((prev) => {
        const next = new Set(prev);
        next.add(soundId);
        return next;
      });
      naturalVolumesRef.current[soundId] = volume;
      setNaturalLoadingFlag(soundId, true);

      try {
let sourceChanged = false;
let nextSource = "";

if (!nextSource) {
  naturalAbortRef.current[soundId]?.abort();
  nextSource = await resolveAudioSource(ns.file);
}

if (naturalOpTokenRef.current[soundId] !== token) return false;
if (naturalRefs.current[soundId] !== el || !transportShouldPlayRef.current) return false;

el.crossOrigin = "anonymous";

        if (!nextSource) throw new Error("natural source missing");

        const absoluteNextSource = nextSource.startsWith("blob:") ? nextSource : new URL(nextSource, window.location.origin).href;
        if (el.src !== absoluteNextSource) {
          el.src = nextSource;
          sourceChanged = true;
        }

        el.preload = "auto";
        el.loop = true;
        el.muted = false;
        audioManager.setupElement(el, `ai-amb-${soundId}`);
        audioManager.setVolume(`ai-amb-${soundId}`, 0, 0);

        if (sourceChanged || el.readyState === 0) {
          try {
            el.load();
          } catch {}
        }

if (el.readyState < 3) await waitUntilPlayable(el, 8000);
if (naturalOpTokenRef.current[soundId] !== token) return false;
if (naturalRefs.current[soundId] !== el || !transportShouldPlayRef.current) return false;
const ok = await audioManager.play(el);

        if (naturalOpTokenRef.current[soundId] !== token) return false;
        if (!transportShouldPlayRef.current || naturalRefs.current[soundId] !== el) { try { el.pause(); } catch {} return false; }
        if (!ok || el.paused) throw new Error("natural play failed");

        audioManager.setVolume(`ai-amb-${soundId}`, volume * NATURAL_GAIN, AMBIANCE_START_FADE_MS);
        return true;
      } catch (error) {
        if (naturalOpTokenRef.current[soundId] !== token || naturalRefs.current[soundId] !== el) return false;
        activeNaturalsRef.current.delete(soundId);
        setActiveNaturals((prev) => {
          const next = new Set(prev);
          next.delete(soundId);
          return next;
        });
        audioManager.setVolume(`ai-amb-${soundId}`, 0, 0);
        try {
          el.pause();
          el.currentTime = 0;
        } catch {}
        console.warn("Natural sound start failed:", error);
        return false;
} finally {
  if (naturalOpTokenRef.current[soundId] === token && naturalRefs.current[soundId] === el) setNaturalLoadingFlag(soundId, false);
}
    },
    [naturalSounds, isPremium, setNaturalLoadingFlag, trackedTimeout]
  );

useEffect(() => {
  if (!isPlaying || isCountingDown || initialAmbiancesAppliedRef.current) return;
  initialAmbiancesAppliedRef.current = true;
  aiAmbiances.forEach((ambiance) => {
    if (activeNaturalsRef.current.has(ambiance.id)) return;
    const volume = Math.max(0, Math.min(1, Number(ambiance.volume) / NATURAL_GAIN));
    void toggleNaturalSound(ambiance.id, Number.isFinite(volume) ? volume : 0.7);
  });
}, [aiAmbiances, isPlaying, isCountingDown, toggleNaturalSound]);

const changeNaturalVolume = useCallback((soundId, volume) => {
  const safeVolume = Math.max(0, Math.min(1, Number(volume) || 0));

  naturalVolumesRef.current[soundId] = safeVolume;

  if (finalFadeStartedRef.current) return;

  const element = naturalRefs.current[soundId];

  if (element) {
    audioManager.ensureElement(element, `ai-amb-${soundId}`);
  }

  audioManager.setLiveVolume(`ai-amb-${soundId}`, safeVolume * NATURAL_GAIN);
}, []);


  const syncBreathingToActive = useCallback((force = false) => {
    if (!session?.breathing) return;

    const breathing = breathingRef.current;
    if (!force || !breathing?.src || !breathing.paused || breathing.seeking || breathing.readyState < 1) return;

    const duration = Number(breathing.duration);
    const sessionTime = Math.max(0, Number(elapsedRef.current) || 0);
    const targetTime = Number.isFinite(duration) && duration > 0 ? sessionTime % duration : sessionTime;
    const drift = Math.abs((Number(breathing.currentTime) || 0) - targetTime);

    if (drift > FORCED_SYNC_TOLERANCE) {
      try {
        breathing.currentTime = targetTime;
      } catch {}
    }
  }, [session?.breathing]);

  const pauseSupportLayers = useCallback(() => {
    try {
      breathingRef.current?.pause();
    } catch {}

    activeNaturalsRef.current.forEach((id) => {
      try {
        naturalRefs.current[id]?.pause();
      } catch {}
    });
  }, []);

  const fadeAllForPause = useCallback((fadeMs = PAUSE_FADE_MS) => {
  const fadeElement = (element, id) => {
    if (!element?.src || element.paused) return;
    audioManager.ensureElement(element, id);
    audioManager.setVolume(id, 0, fadeMs);
  };

  fadeElement(trackARef.current, "ai-trackA");
  fadeElement(trackBRef.current, "ai-trackB");
  fadeElement(breathingRef.current, "ai-breathing");
  activeNaturalsRef.current.forEach((id) => fadeElement(naturalRefs.current[id], `ai-amb-${id}`));
}, []);

  const ensureAIStartupLayers = useCallback(() => {
    if (!aiLayersStartedRef.current) {
      const nextIds = [];

      aiAmbiances.forEach((ambiance) => {
        const element = naturalRefs.current[ambiance.id];
        if (!element?.src || element.readyState < 3) return;

        const sliderVolume = Math.max(0, Math.min(1, Number(ambiance.volume) / NATURAL_GAIN));
        naturalVolumesRef.current[ambiance.id] = Number.isFinite(sliderVolume) ? sliderVolume : 0.7;
        nextIds.push(ambiance.id);
      });

      activeNaturalsRef.current = new Set(nextIds);
      setActiveNaturals(new Set(nextIds));
      aiLayersStartedRef.current = true;
    }

    const layers = [];
    const breathing = breathingRef.current;

    if (breathingStartEnabledRef.current && breathing?.src) {
      layers.push({ element: breathing, id: "ai-breathing", kind: "breathing", soundId: null });
    }

    activeNaturalsRef.current.forEach((soundId) => {
      const element = naturalRefs.current[soundId];
      if (element?.src && element.readyState >= 3) layers.push({ element, id: `ai-amb-${soundId}`, kind: "natural", soundId });
    });

    return layers;
  }, [aiAmbiances]);

  const removeFailedAIStartupLayers = useCallback((layers, results = [], requirePlaying = false) => {
    const failedNaturalIds = new Set();

    layers.forEach((layer, index) => {
      if (results[index] && (!requirePlaying || !layer.element.paused)) return;

      audioManager.setVolume(layer.id, 0, 0);
      pauseAndResetMedia([layer.element], 0);

      if (layer.kind === "breathing") {
        breathingStartEnabledRef.current = false;
        setIsBreathingMuted(true);
        return;
      }

      if (layer.soundId) failedNaturalIds.add(layer.soundId);
    });

    if (failedNaturalIds.size === 0) return;

    activeNaturalsRef.current = new Set([...activeNaturalsRef.current].filter((soundId) => !failedNaturalIds.has(soundId)));
    setActiveNaturals(new Set(activeNaturalsRef.current));
  }, []);

  const armAIPlayback = useCallback(async () => {
    const active = getActiveAudio();

    if (!active?.src || isLoading || audioError || finishedRef.current) return false;
    if (transportArmedRef.current) return true;

    const activeKey = getActiveKey();
    const layers = ensureAIStartupLayers();
    const elements = [active, ...layers.map((layer) => layer.element)];

    startupPreparingRef.current = true;
    transportShouldPlayRef.current = false;
    pauseAndResetMedia(elements, 0);
    audioManager.ensureElement(active, activeKey);
    audioManager.setVolume(activeKey, 0, 0);
    active.muted = false;

    layers.forEach((layer) => {
      audioManager.ensureElement(layer.element, layer.id);
      audioManager.setVolume(layer.id, 0, 0);
      layer.element.muted = false;
    });

    let unlockRequest;

    try {
      unlockRequest = audioManager.unlock();
    } catch {
      unlockRequest = Promise.resolve();
    }

    const armRequest = armMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
    const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), armRequest]);

    removeFailedAIStartupLayers(layers, result.optionalResults, false);
    startupPreparingRef.current = false;

    if (!result.mainArmed) {
      pauseAndResetMedia(elements, 0);
      return false;
    }

    transportArmedRef.current = true;
    return true;
  }, [audioError, ensureAIStartupLayers, getActiveAudio, getActiveKey, isLoading, removeFailedAIStartupLayers]);

  const primeAIPlayback = useCallback(async () => {
    if (!transportArmedRef.current) {
      const armed = await armAIPlayback();
      if (!armed) return false;
    }

    const active = getActiveAudio();
    if (!active?.src) return false;

    const activeKey = getActiveKey();
    const layers = ensureAIStartupLayers();
    const elements = [active, ...layers.map((layer) => layer.element)];

    startupPreparingRef.current = true;
    transportShouldPlayRef.current = true;
    bufferingRef.current = false;
    pauseAndResetMedia(elements, 0);
    audioManager.ensureElement(active, activeKey);
    audioManager.setVolume(activeKey, 0, 0);

    layers.forEach((layer) => {
      audioManager.ensureElement(layer.element, layer.id);
      audioManager.setVolume(layer.id, 0, 0);
    });

    let unlockRequest;

    try {
      unlockRequest = audioManager.unlock();
    } catch {
      unlockRequest = Promise.resolve();
    }

    const startRequest = startMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
    const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), startRequest]);

    removeFailedAIStartupLayers(layers, result.optionalResults, true);

    if (!result.mainStarted || active.paused) {
      startupPreparingRef.current = false;
      transportShouldPlayRef.current = false;
      pauseAndResetMedia(elements, 0);
      return false;
    }

    return true;
  }, [armAIPlayback, ensureAIStartupLayers, getActiveAudio, getActiveKey, removeFailedAIStartupLayers]);

  const revealAIPlayback = useCallback(() => {
    const active = getActiveAudio();

    if (!transportArmedRef.current || !active?.src || active.paused) return false;

    const activeKey = getActiveKey();
    const targetVolume = baseVolumeLiveRef.current;

    if ((Number(active.currentTime) || 0) > REVEAL_REANCHOR_TOLERANCE) {
      try { active.currentTime = 0; } catch {}
    }

    startupPreparingRef.current = false;
    transportArmedRef.current = false;
    transportShouldPlayRef.current = true;
    bufferingRef.current = false;
    audioManager.setVolume(activeKey, targetVolume, START_FADE_MS);

    ensureAIStartupLayers().forEach((layer) => {
      if (layer.element.paused) return;

      if (layer.kind === "breathing") {
        audioManager.setVolume(layer.id, isBreathingMuted ? 0 : targetVolume * BREATHING_VOLUME_RATIO, BREATHING_START_FADE_MS);
        return;
      }

      const volume = naturalVolumesRef.current[layer.soundId] ?? 0.7;
      audioManager.setVolume(layer.id, volume * NATURAL_GAIN, START_FADE_MS);
    });

    const startedAt = active.currentTime || 0;
    elapsedRef.current = startedAt;
    setGlobalElapsed(startedAt);
    setIsPlaying(true);
    syncBreathingToActive(true);
    requestWakeLock();

    /* La séance n'existe en mémoire qu'à partir du moment où elle est
       réellement écoutée : aucune trace si le player n'a jamais démarré. */
    if (!sessionStartLoggedRef.current && user?.uid && session) {
      sessionStartLoggedRef.current = true;
      Promise.resolve(saveSessionStart(user.uid, session, stateBefore)).catch((error) => {
        console.warn("Auria session start not saved.", error);
      });
    }

    return true;
  }, [ensureAIStartupLayers, getActiveAudio, getActiveKey, isBreathingMuted, requestWakeLock, session, stateBefore, syncBreathingToActive, user?.uid]);

  const failAIPlayback = useCallback(() => {
    startupPreparingRef.current = false;
    transportArmedRef.current = false;
    transportShouldPlayRef.current = false;
    bufferingRef.current = false;
    pauseAndResetMedia([trackARef.current, trackBRef.current, breathingRef.current, ...Object.values(naturalRefs.current)], 0);
    setIsCountingDown(false);
    setIsPlaying(false);
    setAudioError("La lecture audio n’est pas disponible sur cet appareil.");
  }, []);

  const resumeAITransport = useCallback(async () => {
    const active = getActiveAudio();
    if (!active?.src || finishedRef.current) return false;

    const activeKey = getActiveKey();
    const targetVolume = baseVolumeLiveRef.current;
    const layers = ensureAIStartupLayers();

    audioManager.ensureElement(active, activeKey);
    audioManager.setVolume(activeKey, 0, 0);

    layers.forEach((layer) => {
      audioManager.ensureElement(layer.element, layer.id);
      audioManager.setVolume(layer.id, 0, 0);

      if (layer.kind === "breathing") syncBreathingToActive(true);
    });

    transportShouldPlayRef.current = true;
    bufferingRef.current = false;

    let unlockRequest;

    try {
      unlockRequest = audioManager.unlock();
    } catch {
      unlockRequest = Promise.resolve();
    }

    const startRequest = startMediaGroup(active, layers.map((layer) => layer.element), (element) => audioManager.play(element));
    const [, result] = await Promise.all([Promise.resolve(unlockRequest).catch(() => {}), startRequest]);

    removeFailedAIStartupLayers(layers, result.optionalResults, true);

    if (!result.mainStarted || active.paused) {
      transportShouldPlayRef.current = false;
      bufferingRef.current = false;
      return false;
    }

    audioManager.setVolume(activeKey, targetVolume, RESUME_FADE_MS);

    ensureAIStartupLayers().forEach((layer) => {
      if (layer.element.paused) return;

      if (layer.kind === "breathing") {
        audioManager.setVolume(layer.id, isBreathingMuted ? 0 : targetVolume * BREATHING_VOLUME_RATIO, RESUME_FADE_MS);
        return;
      }

      const volume = naturalVolumesRef.current[layer.soundId] ?? 0.7;
      audioManager.setVolume(layer.id, volume * NATURAL_GAIN, RESUME_FADE_MS);
    });

    return true;
  }, [ensureAIStartupLayers, getActiveAudio, getActiveKey, isBreathingMuted, removeFailedAIStartupLayers, syncBreathingToActive]);

  const completeCrossfadeNow = useCallback(() => {
    const state = crossfadeStateRef.current;
    if (!state) return;

    if (state.timeoutId) {
      window.clearTimeout(state.timeoutId);
      pendingTimeoutsRef.current.delete(state.timeoutId);
    }

    try {
      state.fromEl?.pause();
    } catch {}

    audioManager.setVolume(state.fromKey, 0, 0);
    audioManager.setVolume(state.toKey, baseVolume, 0);
    activeKeyRef.current = state.toLabel;
    setCurrentTrackIdx(state.nextIdx);
    crossfadingRef.current = false;
    crossfadeStateRef.current = null;
    bufferingRef.current = false;

    const afterNext = tracks[state.nextIdx + 1];
    const inactive = activeKeyRef.current === "A" ? trackBRef.current : trackARef.current;
    if (afterNext && inactive) {
      void resolveAudioSource(afterNext.file).then((source) => {
        if (activeKeyRef.current === state.toLabel && inactive) { inactive.src = source; inactive.load(); }
      }).catch(() => {});
    }

    if (transportShouldPlayRef.current && state.toEl && !state.toEl.paused) {
      setIsPlaying(true);
    }
  }, [baseVolume, tracks]);

  const cancelCrossfade = useCallback(() => {
    const state = crossfadeStateRef.current;
    if (state?.timeoutId) {
      window.clearTimeout(state.timeoutId);
      pendingTimeoutsRef.current.delete(state.timeoutId);
    }

    crossfadeStateRef.current = null;
    crossfadingRef.current = false;
  }, []);

  const togglePlayPause = useCallback(async () => {
    if (isTransitioning || isLoading || audioError) return;

    let active = getActiveAudio();
    if (!active) return;

    setIsTransitioning(true);

    try {
      const anyMainPlaying = [trackARef.current, trackBRef.current].some((element) => element && !element.paused);

if (anyMainPlaying) {
  progressEpochRef.current += 1;
  transportShouldPlayRef.current = false;
  bufferingRef.current = false;

  if (progressRafRef.current) {
    cancelAnimationFrame(progressRafRef.current);
    progressRafRef.current = 0;
  }

  const trackStart = cumulativeStartTimes[currentTrackIdx] || 0;
  const rawElapsed = Math.max(elapsedRef.current, trackStart + (Number(active.currentTime) || 0));
  const frozenElapsed = totalDuration > 0 ? Math.min(rawElapsed, totalDuration) : rawElapsed;

  elapsedRef.current = frozenElapsed;

  if (crossfadeStateRef.current) {
    completeCrossfadeNow();
  }

  if (finalFadeStartedRef.current) {
    finalFadeStartedRef.current = false;
    finalFadeEndAtRef.current = 0;

    if (finalFadeTimeoutRef.current) {
      window.clearTimeout(finalFadeTimeoutRef.current);
      pendingTimeoutsRef.current.delete(finalFadeTimeoutRef.current);
      finalFadeTimeoutRef.current = null;
    }
  }

  fadeAllForPause();
  setGlobalElapsed(frozenElapsed);
  setIsPlaying(false);
  releaseWakeLock();

  await new Promise((resolve) => trackedTimeout(resolve, PAUSE_FADE_MS));

  try {
    trackARef.current?.pause();
    trackBRef.current?.pause();
  } catch {}

  pauseSupportLayers();
  return;
}

      if (active.ended) {
        transportShouldPlayRef.current = false;
        finishedRef.current = true;
        finishSessionRef.current?.("ended");
        return;
      }

      const resumed = await resumeAITransport();

      if (!resumed) throw new Error("La reprise audio a échoué.");

      setIsPlaying(true);
      requestWakeLock();
    } catch (error) {
      transportShouldPlayRef.current = false;
      setIsPlaying(false);
      setAudioError("Impossible de reprendre la lecture audio.");
      console.warn("toggle resume failed", error);
    } finally {
      setIsTransitioning(false);
    }
}, [
  audioError,
  completeCrossfadeNow,
  cumulativeStartTimes,
  currentTrackIdx,
  fadeAllForPause,
  getActiveAudio,
  isLoading,
  isTransitioning,
  pauseSupportLayers,
  releaseWakeLock,
  requestWakeLock,
  resumeAITransport,
  totalDuration,
  trackedTimeout,
]);

  const crossfadeToNext = useCallback(
    async (nextIdx) => {
      if (crossfadingRef.current || !transportShouldPlayRef.current) return;
      crossfadingRef.current = true;

      const next = tracks[nextIdx];
      if (!next) {
        crossfadingRef.current = false;
        return;
      }

      const fromLabel = activeKeyRef.current;
      const fromKey = `ai-track${fromLabel}`;
      const toLabel = fromLabel === "A" ? "B" : "A";
      const toKey = `ai-track${toLabel}`;
      const fromEl = fromLabel === "A" ? trackARef.current : trackBRef.current;
      const toEl = toLabel === "A" ? trackARef.current : trackBRef.current;

      if (!fromEl || !toEl) {
        crossfadingRef.current = false;
        return;
      }

      let resolvedNextFile;
      try {
        resolvedNextFile = await resolveAudioSource(next.file);
      } catch (error) {
        console.warn("crossfade source failed", error);
        crossfadingRef.current = false;
        setAudioError("Impossible de charger la suite de la session.");
        return;
      }
      if (!transportShouldPlayRef.current || !crossfadingRef.current) return;
      const targetSrc = new URL(resolvedNextFile, window.location.origin).href;
      if (toEl.src !== targetSrc) {
        toEl.src = resolvedNextFile;
        try {
          toEl.load();
        } catch {}
      }

      try {
        toEl.currentTime = 0;
        audioManager.ensureElement(toEl, toKey);
        audioManager.setVolume(toKey, 0, 0);

        if (toEl.readyState < 3) await waitUntilPlayable(toEl, 8000);
        const started = await audioManager.play(toEl);
        if (!crossfadingRef.current || !transportShouldPlayRef.current) { audioManager.cancelPendingPlay(toEl); toEl.pause(); return; }
        if (!started || toEl.paused) throw new Error("La piste suivante n'a pas démarré.");
      } catch (error) {
        console.warn("crossfade play failed", error);
        transportShouldPlayRef.current = false;
        bufferingRef.current = false;
        crossfadingRef.current = false;
        crossfadeStateRef.current = null;
        try {
          fromEl.pause();
          toEl.pause();
        } catch {}
        pauseSupportLayers();
        setIsPlaying(false);
        setAudioError("Impossible de charger la suite de la session.");
        return;
      }

      const fadeMs = CROSSFADE_SECONDS * 1000;
      audioManager.setVolume(fromKey, 0, fadeMs);
      audioManager.setVolume(toKey, baseVolume, fadeMs);

      const state = { fromEl, fromKey, nextIdx, timeoutId: 0, toEl, toKey, toLabel };
      crossfadeStateRef.current = state;
      state.timeoutId = trackedTimeout(() => {
        if (crossfadeStateRef.current !== state) return;
        completeCrossfadeNow();
      }, fadeMs + 100);
    },
    [baseVolume, completeCrossfadeNow, pauseSupportLayers, trackedTimeout, tracks]
  );

  const completeFinalFade = useCallback(() => {
  if (finishedRef.current) return;

  const completedSeconds = durationLimit || totalDuration || Math.max(0, elapsedRef.current);

  if (finalFadeTimeoutRef.current) {
    window.clearTimeout(finalFadeTimeoutRef.current);
    pendingTimeoutsRef.current.delete(finalFadeTimeoutRef.current);
    finalFadeTimeoutRef.current = null;
  }

  finalFadeStartedRef.current = false;
  finalFadeEndAtRef.current = 0;
  transportShouldPlayRef.current = false;
  bufferingRef.current = false;
  finishedRef.current = true;
  progressEpochRef.current += 1;
  elapsedRef.current = completedSeconds;

  setGlobalElapsed(completedSeconds);
  finishSessionRef.current?.("ended", completedSeconds);
}, [durationLimit, totalDuration]);

const beginFinalFade = useCallback(() => {
  if (finalFadeStartedRef.current || finishedRef.current) return;

  finalFadeStartedRef.current = true;
  finalFadeEndAtRef.current = performance.now() + FINAL_FADE_MS;
  bufferingRef.current = false;

  cancelCrossfade();

const fadeElement = (element, id) => {
  if (!element?.src || element.paused) return;

  audioManager.ensureElement(element, id);

  if (typeof audioManager.fadeOut === "function") {
    audioManager.fadeOut(id, FINAL_FADE_MS);
  } else {
    audioManager.setVolume(id, 0, FINAL_FADE_MS);
  }
};

  fadeElement(trackARef.current, "ai-trackA");
  fadeElement(trackBRef.current, "ai-trackB");
  fadeElement(breathingRef.current, "ai-breathing");

  const naturalIds = new Set([
    ...activeNaturalsRef.current,
    ...Object.keys(naturalRefs.current).filter((id) => {
      const element = naturalRefs.current[id];
      return Boolean(element?.src && !element.paused);
    }),
  ]);

  naturalIds.forEach((id) => {
    const element = naturalRefs.current[id];
    if (!element?.src || element.paused) return;

    naturalOpTokenRef.current[id] = (naturalOpTokenRef.current[id] || 0) + 1;
    audioManager.ensureElement(element, `ai-amb-${id}`);
if (typeof audioManager.fadeOut === "function") {
  audioManager.fadeOut(`ai-amb-${id}`, FINAL_FADE_MS);
} else {
  audioManager.setVolume(`ai-amb-${id}`, 0, FINAL_FADE_MS);
}
  });
}, [cancelCrossfade]);

const handleTrackEnded = useCallback((event) => {
  if (crossfadingRef.current || event.currentTarget !== getActiveAudio()) return;

  if (currentTrackIdx < tracks.length - 1) {
    crossfadeToNext(currentTrackIdx + 1);
    return;
  }

  if (!finalFadeStartedRef.current) {
    completeFinalFade();
    return;
  }

  const remainingFadeMs = Math.max(0, finalFadeEndAtRef.current - performance.now());

  if (remainingFadeMs <= 40) {
    completeFinalFade();
    return;
  }

  if (finalFadeTimeoutRef.current) {
    window.clearTimeout(finalFadeTimeoutRef.current);
    pendingTimeoutsRef.current.delete(finalFadeTimeoutRef.current);
  }

  finalFadeTimeoutRef.current = trackedTimeout(() => {
    finalFadeTimeoutRef.current = null;
    completeFinalFade();
  }, remainingFadeMs + 20);
}, [completeFinalFade, crossfadeToNext, currentTrackIdx, getActiveAudio, trackedTimeout, tracks.length]);

const tickRef = useRef(null);

tickRef.current = () => {
  if (startupPreparingRef.current) return;

  const active = getActiveAudio();

  if (!active || active.paused || !transportShouldPlayRef.current || finishedRef.current) return;

  const progressEpoch = progressEpochRef.current;
  const trackTime = active.currentTime || 0;
  const trackStart = cumulativeStartTimes[currentTrackIdx] || 0;
  const globalTime = trackStart + trackTime;
  const nextElapsed = Math.min(globalTime, totalDuration);
  const monotonicElapsed = Math.max(elapsedRef.current, nextElapsed);

if (monotonicElapsed > elapsedRef.current) {
  elapsedRef.current = monotonicElapsed;

  const now = performance.now();

  if (now - lastProgressPaintRef.current >= PROGRESS_PAINT_MS) {
    lastProgressPaintRef.current = now;

    setGlobalElapsed((previous) => {
      if (progressEpochRef.current !== progressEpoch) return previous;
      return Math.max(previous, monotonicElapsed);
    });
  }
}

  if (breathingRef.current?.src && !breathingRef.current.paused) {
    syncBreathingToActive(false);
  }

  const currentTrack = tracks[currentTrackIdx];
  if (!currentTrack) return;

  const isLastTrack = currentTrackIdx === tracks.length - 1;
  const playUntil = Number(currentTrack.playUntil) || 0;
  const remainingToLimit = totalDuration > 0 ? totalDuration - globalTime : Infinity;

  let remainingToEnd = remainingToLimit;

  if (isLastTrack) {
    const candidates = [];

    if (playUntil > 0) candidates.push(playUntil - trackTime);
    if (Number.isFinite(active.duration) && active.duration > 0) candidates.push(active.duration - trackTime);

    if (candidates.length) {
      remainingToEnd = Math.min(remainingToEnd, Math.max(0, Math.min(...candidates)));
    }
  }

if (finalFadeStartedRef.current) {
  const fadeFinished = performance.now() >= finalFadeEndAtRef.current;

  if (remainingToEnd <= 0 && fadeFinished) {
    completeFinalFade();
  }

  return;
}

if (remainingToEnd <= 0) {
  completeFinalFade();
  return;
}

if (remainingToEnd <= FINAL_FADE_LEAD_SECONDS) {
  beginFinalFade();
  return;
}

  if (!isLastTrack && !crossfadingRef.current && trackTime >= playUntil - CROSSFADE_SECONDS && trackTime < playUntil - 0.1) {
    crossfadeToNext(currentTrackIdx + 1);
  }
};

useEffect(() => {
  if (!isPlaying) {
    if (progressRafRef.current) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = 0;
    }

    return undefined;
  }

  const frame = () => {
    tickRef.current?.();

    if (isPlayingRef.current && transportShouldPlayRef.current && !finishedRef.current) {
      progressRafRef.current = requestAnimationFrame(frame);
      return;
    }

    progressRafRef.current = 0;
  };

  progressRafRef.current = requestAnimationFrame(frame);

  return () => {
    if (progressRafRef.current) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = 0;
    }
  };
}, [isPlaying]);

useEffect(() => {
  const decks = [trackARef.current, trackBRef.current].filter(Boolean);
  const clearBufferingTimer = () => {
    if (!bufferingTimerRef.current) return;
    window.clearTimeout(bufferingTimerRef.current);
    bufferingTimerRef.current = null;
  };
  const onBuffering = (event) => {
    if (startupPreparingRef.current) return;
    const deck = event.currentTarget;
    if (deck !== getActiveAudio() || !transportShouldPlayRef.current) return;

    clearBufferingTimer();
    bufferingTimerRef.current = window.setTimeout(() => {
      bufferingTimerRef.current = null;
      if (deck !== getActiveAudio() || !transportShouldPlayRef.current || deck.paused || deck.readyState >= 3) return;

      if (crossfadeStateRef.current) {
        completeCrossfadeNow();
        const nextActive = getActiveAudio();
        if (nextActive && !nextActive.paused && nextActive.readyState >= 3) return;
      }

      bufferingRef.current = true;
      setIsPlaying(false);
      pauseSupportLayers();
    }, 180);
  };
  const onPlaying = (event) => {
    if (startupPreparingRef.current || event.currentTarget !== getActiveAudio() || !transportShouldPlayRef.current) return;

    clearBufferingTimer();
    const wasBuffering = bufferingRef.current;
    bufferingRef.current = false;

    if (wasBuffering) {
      void resumeAITransport().finally(() => {
        if (transportShouldPlayRef.current) {
          setIsPlaying(true);
          requestWakeLock();
        }
      });
      return;
    }

    setIsPlaying(true);
    requestWakeLock();
  };

  decks.forEach((deck) => {
    deck.addEventListener("waiting", onBuffering);
    deck.addEventListener("playing", onPlaying);
  });

  return () => {
    clearBufferingTimer();
    decks.forEach((deck) => {
      deck.removeEventListener("waiting", onBuffering);
      deck.removeEventListener("playing", onPlaying);
    });
  };
}, [completeCrossfadeNow, getActiveAudio, pauseSupportLayers, requestWakeLock, resumeAITransport]);

const handleBaseVolumeInput = useCallback((event) => {
  const nextVolume = Math.max(0, Math.min(1, Number(event.currentTarget.value) || 0));

  baseVolumeLiveRef.current = nextVolume;

  if (finalFadeStartedRef.current) return;

  const active = getActiveAudio();
  const activeKey = getActiveKey();

  if (active) {
    audioManager.ensureElement(active, activeKey);
  }

  audioManager.setLiveVolume(activeKey, nextVolume);

  const breathing = breathingRef.current;

  if (breathing?.src) {
    audioManager.ensureElement(breathing, "ai-breathing");
    audioManager.setLiveVolume("ai-breathing", isBreathingMuted ? 0 : nextVolume * BREATHING_VOLUME_RATIO);
  }
}, [getActiveAudio, getActiveKey, isBreathingMuted]);

const commitBaseVolume = useCallback(() => {
  const nextVolume = baseVolumeLiveRef.current;

  setBaseVolume((currentVolume) => currentVolume === nextVolume ? currentVolume : nextVolume);
}, []);

const toggleBreathingMute = useCallback(async () => {
  if (finalFadeStartedRef.current || finishedRef.current) return;

  const breathing = breathingRef.current;
  if (!breathing?.src) return;

  const nextMuted = !isBreathingMuted;
  setIsBreathingMuted(nextMuted);
  audioManager.ensureElement(breathing, "ai-breathing");

  const active = getActiveAudio();

  if (!nextMuted && active && !active.paused && breathing.paused) {
    breathingStartEnabledRef.current = true;
    syncBreathingToActive(true);
    await audioManager.play(breathing);
    syncBreathingToActive(true);
  }

  audioManager.setLiveVolume("ai-breathing", nextMuted ? 0 : baseVolumeLiveRef.current * BREATHING_VOLUME_RATIO);
}, [getActiveAudio, isBreathingMuted, syncBreathingToActive]);

  useEffect(() => {
    if (!showVolume) return;
    const onPointerDown = (e) => {
      if (
        volumePopoverRef.current?.contains(e.target) ||
        volumeBtnRef.current?.contains(e.target)
      )
        return;
      setShowVolume(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [showVolume]);

  const stopAllNaturals = useCallback(() => {
    activeNaturalsRef.current.forEach((id) => {
      naturalOpTokenRef.current[id] = (naturalOpTokenRef.current[id] || 0) + 1;
      audioManager.setVolume(`ai-amb-${id}`, 0, 80);
      trackedTimeout(() => {
        const el = naturalRefs.current[id];
        if (el) {
          try {
            el.pause();
            el.currentTime = 0;
          } catch {}
        }
      }, 120);
    });
    activeNaturalsRef.current = new Set();
    setActiveNaturals(new Set());
  }, [trackedTimeout]);

const buildExactSessionForFavorite = useCallback(() => {
  if (!session) return session;

  const activeIds = Array.from(activeNaturalsRef.current || []);

  const exactAmbiances = activeIds
    .map((id) => {
      const originalAmbiance = (session.ambiances || []).find((a) => a.id === id);
      const naturalSound = naturalSounds.find((s) => s.id === id);

      const sliderVolume =
        typeof naturalVolumesRef.current[id] === "number"
          ? naturalVolumesRef.current[id]
          : typeof originalAmbiance?.volume === "number"
          ? Math.min(1, originalAmbiance.volume / NATURAL_GAIN)
          : 0.7;

      return {
        ...(originalAmbiance || {}),
        id,
        name: originalAmbiance?.name || naturalSound?.name || id,
        volume: Math.max(0, Math.min(1, sliderVolume * NATURAL_GAIN)),
      };
    })
    .filter(Boolean);

    const exactBreathing = session.breathing || null;

  return {
    ...session,

    // ✅ Ambiances exactement actives au moment de sauvegarder
    ambiances: exactAmbiances,

    // ✅ Respiration conservée au top-level
    breathing: exactBreathing,

    context: {
      ...(session.context || {}),
      withBreathing: true,
      savedFromPlayer: true,

      // ✅ Backup dans context pour les favoris/replays anciens ou incomplets
      breathing: exactBreathing,
      breathingId: exactBreathing?.id || null,
      breathingName: exactBreathing?.name || null,
      breathingFile: exactBreathing?.file || null,
    },
  };
}, [session, naturalSounds]);

const finishSession = useCallback(
  (reason, forcedSeconds = null) => {
    transportShouldPlayRef.current = false;
    transportArmedRef.current = false;
    startupPreparingRef.current = false;
    bufferingRef.current = false;
    cancelCrossfade();

    const exactSession = buildExactSessionForFavorite();
    const completedNaturally = reason === "ended";
    const rawSeconds = Number.isFinite(Number(forcedSeconds)) ? Number(forcedSeconds) : elapsedRef.current;
    const seconds = Math.max(0, Math.round(rawSeconds));

    try {
      trackARef.current?.pause();
      trackBRef.current?.pause();
      breathingRef.current?.pause();
    } catch {}

    stopAllNaturals();
    setIsPlaying(false);
    releaseWakeLock();
    setListenedSeconds(seconds);

    if (seconds > 0 && !streakCountedRef.current && typeof markTodayActive === "function") {
      streakCountedRef.current = true;
      markTodayActive().catch((error) => {
        console.warn("Streak update failed:", error);
      });
    }

    if (seconds > 0 && !aiSessionLoggedRef.current && typeof onFinalize === "function") {
      aiSessionLoggedRef.current = true;
      onFinalize(exactSession, seconds, reason || "finish");
    }

    if (completedNaturally || seconds >= FEEDBACK_MIN_LISTEN_SECONDS) {
      setFeedbackSession(exactSession);
      setShowFeedback(true);
      return;
    }

    onDoneRedirect();
  },
  [buildExactSessionForFavorite, cancelCrossfade, markTodayActive, onDoneRedirect, onFinalize, releaseWakeLock, stopAllNaturals],
);

    useEffect(() => {
    finishSessionRef.current = finishSession;
  }, [finishSession]);

    useEffect(() => {
    return () => {
      // Sortie non gérée (retour navigateur, fermeture) : on compte la session
      // si elle a été écoutée, sans feedback (page déjà quittée).
      const seconds = Math.round(elapsedRef.current);
      if (seconds <= 0) return;

      if (!streakCountedRef.current && typeof markTodayActive === "function") {
        streakCountedRef.current = true;
        markTodayActive().catch(() => {});
      }
      if (!aiSessionLoggedRef.current && typeof onFinalize === "function") {
        aiSessionLoggedRef.current = true;
        onFinalize(buildExactSessionForFavorite(), seconds, "unmount");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const handleBack = useCallback(() => {
  transportShouldPlayRef.current = false;
  bufferingRef.current = false;
  cancelCrossfade();
  const exactSession = buildExactSessionForFavorite();

  const seconds = Math.round(globalElapsed);
  setListenedSeconds(seconds);

  try {
    trackARef.current?.pause();
    trackBRef.current?.pause();
    breathingRef.current?.pause();
  } catch {}

  stopAllNaturals();

  setIsPlaying(false);
  releaseWakeLock();

  // ✅ Streak
  if (
    seconds > 0 &&
    !streakCountedRef.current &&
    typeof markTodayActive === "function"
  ) {
    streakCountedRef.current = true;
    markTodayActive().catch((err) => {
      console.warn("Streak update failed:", err);
    });
  }

  // ✅ Progression avec la session exacte
  if (
    seconds > 0 &&
    !aiSessionLoggedRef.current &&
    typeof onFinalize === "function"
  ) {
    aiSessionLoggedRef.current = true;
    onFinalize(exactSession, seconds, "back");
  }

if (isReplay || seconds < FEEDBACK_MIN_LISTEN_SECONDS) {
  onDoneRedirect();
} else {
  setFeedbackSession(exactSession);
  setShowFeedback(true);
}
}, [
  buildExactSessionForFavorite,
  cancelCrossfade,
  globalElapsed,
  stopAllNaturals,
  releaseWakeLock,
  markTodayActive,
  onFinalize,
  isReplay,
  onDoneRedirect,
]);

const requestBack = useCallback(() => {
  if (historyGuardActiveRef.current) {
    browserBackHandledRef.current = false;
    window.history.back();
    return;
  }

  handleBack();
}, [handleBack]);

useEffect(() => {
  const handleBrowserHistoryChange = () => {
    if (suppressBrowserBackRef.current) return;
    if (!historyGuardActiveRef.current) return;
    if (browserBackHandledRef.current) return;

    historyGuardActiveRef.current = false;
    browserBackHandledRef.current = true;

    // Le retour navigateur termine la séance exactement comme
    // le bouton « Terminer », sans démonter le player avant le feedback.
    handleBack();
  };

  window.addEventListener("popstate", handleBrowserHistoryChange);

  return () => {
    window.removeEventListener("popstate", handleBrowserHistoryChange);
  };
}, [handleBack]);

  // Visibility (iOS retour de fond)
  useEffect(() => {
    const onVis = async () => {
      if (document.visibilityState !== "visible") {
        wakeLockRef.current = null;
        return;
      }

      await audioManager.unlock();
      audioManager.resyncVolume("ai-trackA");
      audioManager.resyncVolume("ai-trackB");
      if (hasBreathingAudio) audioManager.resyncVolume("ai-breathing");
      activeNaturalsRef.current.forEach((id) => audioManager.resyncVolume(`ai-amb-${id}`));

      const active = getActiveAudio();
      if (active && active.paused && !active.ended && transportShouldPlayRef.current) {
        try {
          await new Promise((resolve) => window.setTimeout(resolve, 200));
          const resumed = await resumeAITransport();

          if (resumed) {
            bufferingRef.current = false;
            setIsPlaying(true);
          }
        } catch {}
      }

      if (active && !active.paused) requestWakeLock();
    };

    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
}, [getActiveAudio, hasBreathingAudio, requestWakeLock, resumeAITransport]);
  
// Cleanup AudioManager au démontage (entre 2 sessions)
useEffect(() => {
  // ✅ Copie les refs au moment où l'effet s'exécute
  const trackARefCurrent = trackARef;
  const trackBRefCurrent = trackBRef;
  const naturalRefsCurrent = naturalRefs;
  const pendingTimeoutsRefCurrent = pendingTimeoutsRef;
  const wakeLockRefCurrent = wakeLockRef;
  const breathingRefCurrent = breathingRef;
  const naturalOpTokens = naturalOpTokenRef.current;

  return () => {
    transportShouldPlayRef.current = false;
    transportArmedRef.current = false;
    startupPreparingRef.current = false;
    bufferingRef.current = false;
    cancelCrossfade();

    if (bufferingTimerRef.current) window.clearTimeout(bufferingTimerRef.current);
    bufferingTimerRef.current = null;

    Object.keys(naturalOpTokens).forEach((id) => { naturalOpTokens[id] += 1; });
    Object.values(naturalAbortRef.current).forEach((controller) => {
      try {
        controller.abort();
      } catch {}
    });
    naturalAbortRef.current = {};
    naturalPreloadPromiseRef.current = {};

    try { trackARefCurrent.current?.pause(); } catch {}
    try { trackBRefCurrent.current?.pause(); } catch {}
    try { breathingRefCurrent.current?.pause(); } catch {}
    Object.values(naturalRefsCurrent.current).forEach((el) => {
      try { el?.pause(); } catch {}
    });

    audioManager.cleanup("ai-trackA", { hard: true });
    audioManager.cleanup("ai-trackB", { hard: true });
    audioManager.cleanup("ai-breathing", { hard: true });
    (naturalSounds || []).forEach((ns) => {
      audioManager.cleanup(`ai-amb-${ns.id}`, { hard: true });
    });

    pendingTimeoutsRefCurrent.current.forEach((id) => clearTimeout(id));
    pendingTimeoutsRefCurrent.current.clear();

    if (wakeLockRefCurrent.current) {
      try { wakeLockRefCurrent.current.release(); } catch {}
      wakeLockRefCurrent.current = null;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [cancelCrossfade]);

  if (!session) return null;

  if (showFeedback) {

    return (
<AISessionFeedback
  session={feedbackSession || session}
  stateBefore={stateBefore}
  intent={intent}
  listenedSeconds={listenedSeconds}
  onSaveNote={(content) =>
    onSaveNote?.(
      content,
      feedbackSession || session,
      listenedSeconds,
    )
  }
  onDone={onDoneRedirect}
/>
    );
  }
  return (
    <>
      <TopbarInner
        title="Terminer"
        isPremium={isPremium}
        longestStreak={longestStreak}
        onBack={requestBack}
        onTitleClick={requestBack}
        onLogoClick={requestBack}
        titleColor="rgba(54, 73, 91, 0.78)"
      />

      <audio ref={trackARef} preload="auto" playsInline onEnded={handleTrackEnded} onTimeUpdate={() => tickRef.current?.()} style={{ display: "none" }} />
      <audio ref={trackBRef} preload="auto" playsInline onEnded={handleTrackEnded} onTimeUpdate={() => tickRef.current?.()} style={{ display: "none" }} />
      <audio ref={breathingRef} preload="auto" playsInline loop style={{ display: "none" }} />

      <div className="player-container app-fill app-safe--no-top player--full player-with-topbar">
        <div className="player-card player-card--flat">
          {(isLoading || audioError) && (
            <div className="loading-overlay">
              {audioError ? (
                <>
                  <div className="loading-error">⚠️</div>
                  <p className="loading-text">{audioError}</p>
                  <button onClick={onDoneRedirect} className="btn-primary btn-sm">Retour</button>
                </>
              ) : (
                <>
                  <div className="loading-spinner"></div>
                  <p className="loading-text">Préparation de votre composition…</p>
                </>
              )}
            </div>
          )}

          {!isLoading && !audioError && (
            <>
              <div className="player-info">
                <p className="player-info__eyebrow">Respiration guidée</p>

                <h2 className="player-title">{session.breathing.name}</h2>

                {(session.breathing.benefit || session.breathing.reason) && (
                  <p className="player-description">{session.breathing.benefit || session.breathing.reason}</p>
                )}
              </div>

              <div className="player-breathing-zone">
                <button
                  type="button"
                  onClick={() => { if (!isCountingDown) setIsBreathingFullscreen(true); }}
                  className="breathing-fullscreen-trigger"
                  aria-label="Ouvrir l’animation en grand écran"
                  disabled={isCountingDown}
                  title="Passer en plein écran"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, margin: 0, border: "none", background: "transparent", cursor: isCountingDown ? "default" : "pointer", WebkitTapHighlightColor: "transparent" }}
                >
                  <BreathingAnimation
                    technique={session.breathing.id}
                    isPlaying={isPlaying}
                    currentTime={globalElapsed}
                    audioRef={breathingRef}
                  />
                </button>
              </div>

              <div className="player-transport">
                <div className="progress-container">
                  <input
                    type="range"
                    min={0}
                    max={totalDuration || 0}
                    step={0.01}
                    value={totalDuration ? Math.min(globalElapsed, totalDuration) : 0}
                    readOnly
                    tabIndex={-1}
                    className="progress-bar progress-bar--readonly"
                    aria-label="Progression de lecture"
                    aria-readonly="true"
                    onChange={() => {}}
                    onPointerDown={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.preventDefault()}
                    onKeyDown={(e) => e.preventDefault()}
                    style={{ "--p": `${totalDuration ? (globalElapsed / totalDuration) * 100 : 0}%`, pointerEvents: "none", cursor: "default" }}
                  />
                </div>

                <div className="player-controls-row">
                  <div className="player-controls-left">
                    <button
                      onClick={(e) => { togglePlayPause(); e.currentTarget.blur(); }}
                      className="play-button"
                      disabled={isTransitioning}
                      aria-disabled={isTransitioning}
                      aria-label={isPlaying ? "Pause" : "Lecture"}
                    >
                      <span className="play-icon">{isPlaying ? <Pause size={22} /> : <Play size={22} />}</span>
                    </button>

                    <button
                      onClick={(e) => { toggleBreathingMute(); e.currentTarget.blur(); }}
                      className={`icon-btn ${isBreathingMuted ? "is-muted" : ""}`}
                      aria-label={isBreathingMuted ? "Activer respiration" : "Couper respiration"}
                      aria-pressed={!isBreathingMuted}
                    >
                      <Wind size={18} />
                    </button>

                    <div className="volume-wrap">
                      <button
                        ref={volumeBtnRef}
                        onClick={(e) => { setShowVolume((v) => !v); e.currentTarget.blur(); }}
                        className={`icon-btn ${showVolume ? "active" : ""}`}
                        aria-label="Volume"
                        aria-expanded={showVolume}
                      >
                        <Volume2 size={18} />
                      </button>

                      {showVolume && (
                        <div ref={volumePopoverRef} className="volume-popover">
                          <div className="volume-vertical-shell">
                            <input
                              key={user?.uid || "guest"}
                              className="volume-vertical"
                              type="range"
                              min={0}
                              max={1}
                              step={0.01}
                              defaultValue={baseVolume}
                              onInput={handleBaseVolumeInput}
                              onPointerUp={commitBaseVolume}
                              onTouchEnd={commitBaseVolume}
                              onKeyUp={commitBaseVolume}
                              onBlur={commitBaseVolume}
                              aria-label="Volume général"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {(session.breathing?.explanation || session.breathing?.reason || session.narrative || session.why?.length || frequencyDetails.length || session.breathing?.adaptHint) && (
                <div className="player-header player-header--minimal">
                  <details className="aipl-why">
                    <summary className="aipl-why__summary">
                      <span>Détails de la session</span>

                      <svg
                        className="aipl-why__chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </summary>

                    <div className="aipl-why__panel">
                      {(session.breathing?.explanation || session.breathing?.reason) && (
                        <p className="aipl-why__text">{session.breathing.explanation || session.breathing.reason}</p>
                      )}

                      {session.why?.map((reason) => (
                        <p key={reason} className="aipl-why__text aipl-why__text--reason">{reason}</p>
                      ))}

                      {session.narrative && (
                        <p className="aipl-why__text aipl-why__text--narrative">{session.narrative}</p>
                      )}

                      {frequencyDetails.length > 0 && (
                        <div className="aipl-why__freqs" aria-label="Fréquences et ondes utilisées">
                          <div className="aipl-why__freqTitle">Fréquences utilisées</div>

                          <div className="aipl-why__freqGrid">
                            {frequencyDetails.map((frequency, index) => (
                              <div key={`${frequency.hz}-${frequency.type}-${index}`} className="aipl-why__freqCard">
                                <div className="aipl-why__freqItem">
                                  <span>Fréquence</span>
                                  <strong>{frequency.hz}</strong>
                                </div>

                                <div className="aipl-why__freqItem">
                                  <span>Onde</span>
                                  <strong>{frequency.type}</strong>
                                </div>

                                {frequency.note && (
                                  <p className="aipl-why__freqNote">{frequency.note}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {session.breathing?.adaptHint && (
                        <p className="aipl-why__adapt">
                          <span aria-hidden="true">🍃</span>
                          {session.breathing.adaptHint}
                        </p>
                      )}
                    </div>
                  </details>
                </div>
              )}

              <div id="ai-player-natural-sounds" className="natural-sounds-section">
                <h3 className="section-title-small">
                  <span className="section-icon">🌿</span>
                  Sons naturels
                </h3>

                <p className="section-description">
                  Ajoutez des sons d'ambiance pour personnaliser votre expérience
                </p>

                <div className="natural-sounds-grid">
                  {naturalSounds.map((ns) => {
                    if (ns.premium && !isPremium) return null;

                    const active = activeNaturals.has(ns.id);
                    const aiAmb = aiAmbiances.find((a) => a.id === ns.id);
                    const initialVolume = aiAmb ? Math.min(1, aiAmb.volume / NATURAL_GAIN) : 0.7;

                    return (
                      <div key={ns.id} className="natural-sound-item">
                        <NaturalSoundTile
                          sound={ns}
                          active={active}
                          loading={!!naturalLoading[ns.id]}
                          onToggle={toggleNaturalSound}
                          onVolumeChange={changeNaturalVolume}
                          initialVolume={initialVolume}
                        />
                      </div>
                    );
                  })}
                </div>

                {activeNaturals.size > 0 && (
                  <button type="button" onClick={stopAllNaturals} className="stop-all-button">
                    <span>🔇</span>
                    <span>Désactiver tous les sons</span>
                  </button>
                )}
              </div>

              <button type="button" className="finish-button" onClick={requestBack}>
                Terminer la session
              </button>
            </>
          )}

          {naturalSounds.map((ns) => (
            <audio
              key={ns.id}
              ref={(el) => (naturalRefs.current[ns.id] = el)}
              preload="auto"
              playsInline
              crossOrigin="anonymous"
              data-src={ns.file}
              style={{ display: "none" }}
            />
          ))}
        </div>
      </div>

      {isBreathingFullscreen && !audioError && (
        <FullscreenBreathing
          technique={session.breathing.id}
          isPlaying={isPlaying}
          currentTime={globalElapsed}
          audioRef={breathingRef}
          sound={{ name: session.breathing.name }}
          onExit={() => setIsBreathingFullscreen(false)}
        />
      )}

      {isCountingDown && !audioError && (
        <CountdownOverlay
          isReady={!isLoading}
          onArm={armAIPlayback}
          onPrime={primeAIPlayback}
          onComplete={() => { if (!revealAIPlayback()) failAIPlayback(); }}
          onSkip={() => { if (!revealAIPlayback()) failAIPlayback(); }}
          onExited={() => setIsCountingDown(false)}
          onError={failAIPlayback}
        />
      )}
    </>
  );
}
export default AIPlayerPage;
